import React, { Fragment, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  Button,
  Dropdown,
  Input,
  InputOnChangeData,
  Pagination,
  Table,
} from 'semantic-ui-react';
import deburr from 'lodash/deburr';
import { useTable } from 'hooks/useTable';
import { pageSizeOptions } from 'hooks/useTablePaginating';
import { ApplicationSegment } from 'components/ApplicationSegment';
import { Link, useHistory } from 'react-router-dom';
import { EmptyStateMessage } from 'components/EmptyStateMessage';
import { useResponsive } from 'hooks/useResponsive';
import { TeamSummary } from 'modules/team/types';
import { ActiveIndicator } from 'components/ActiveIndicator';
import { TeamLogo } from './TeamLogo';
import { ApplicationLoader } from 'components/ApplicationLoader';
import { ShowInactiveToggle } from 'components/ShowInactiveToggle';

const m = defineMessages({
  recordperpage: {
    id: 'Teams.Table.RecordPerPage',
    defaultMessage: 'Équipes par page',
  },
  totalCount: {
    id: 'Teams.Table.TotalCount',
    defaultMessage: 'Nombre total',
  },
  search: {
    id: 'Teams.Table.Search',
    defaultMessage: 'Rechercher',
  },
  noRow: {
    id: 'Teams.Table.NoRow',
    defaultMessage: "Il n'y a aucune équipe.",
  },
  noMatchFound: {
    id: 'Teams.Table.NoMatchFound',
    defaultMessage: 'Aucune équipe ne correspond à votre recherche.',
  },
  name: {
    id: 'Teams.Table.Name',
    defaultMessage: 'Nom',
  },
  logo: {
    id: 'Teams.Table.Logo',
    defaultMessage: 'Logo',
  },
  userCount: {
    id: 'Teams.Table.UserCount',
    defaultMessage: 'Membres',
  },
  active: {
    id: 'Teams.Table.Active',
    defaultMessage: 'Actif',
  },
  new: {
    id: 'Teams.Table.New',
    defaultMessage: 'Créer une équipe',
  },
});

interface EntryRow {
  id: number;
  name: string;
  userCount: number;
  isActive: boolean;
}

interface Props {
  teams: TeamSummary[];
}

export const TeamsTable: React.FC<Props> = ({ teams }) => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const { isMobile } = useResponsive();
  const {
    currentPageRows,
    setRows,
    hasRows,
    matchFound,
    hasMultiplePages,
    pageCount,
    sortingColumn,
    sortingDirection,
    searchInRows,
    changeSortingColumn,
    currentPage,
    handlePageChange,
    filteredRows,
    pageSize,
    handlePageSizeChange,
    areRowsReady,
    showInactive,
    setShowInactive,
  } = useTable<EntryRow>({
    sortingColumn: 'name',
    sortingDirection: 'ascending',
  });

  const isLoaded = areRowsReady;

  useEffect(() => {
    const rows: EntryRow[] = teams.map((t) => ({
      id: t.id,
      name: t.name,
      userCount: t.userCount,
      isActive: t.isActive,
    }));
    setRows(rows);
  }, [teams, setRows]);

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData
  ) => {
    searchInRows(data.value, (entryRow) =>
      deburr(Object.values([entryRow.id, entryRow.name]).toString())
    );
  };

  const handleRowClick = (id: number) => {
    history.push(`/admin/teams/${id}`);
  };

  if (!isLoaded) {
    return <ApplicationLoader />;
  }

  const totalCountAndRecordPerPage = matchFound && (
    <Fragment>
      <div>
        {formatMessage(m.recordperpage)}:{' '}
        <Dropdown
          inline
          options={pageSizeOptions}
          value={pageSize}
          onChange={handlePageSizeChange}
        />
      </div>
      <div>
        {formatMessage(m.totalCount)}: {filteredRows.length}
      </div>
    </Fragment>
  );

  const searchInput = (
    <Input
      icon="search"
      placeholder={formatMessage(m.search)}
      onChange={handleSearchChange}
    />
  );

  const topBar = hasRows && (
    <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
      <div className="tw-flex-1">{totalCountAndRecordPerPage}</div>
      <div className="tw-flex tw-items-center">
        <ShowInactiveToggle
          showInactive={showInactive}
          onChange={setShowInactive}
        />
        {searchInput}
      </div>
    </div>
  );

  const table = (
    <Fragment>
      <Table
        sortable
        celled
        striped
        selectable
        size="small"
        className="text-sm"
      >
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell
              sorted={sortingColumn === 'name' ? sortingDirection : undefined}
              onClick={changeSortingColumn('name')}
              collapsing
            >
              {formatMessage(m.name)}
            </Table.HeaderCell>
            <Table.HeaderCell>{formatMessage(m.logo)}</Table.HeaderCell>
            <Table.HeaderCell
              sorted={
                sortingColumn === 'userCount' ? sortingDirection : undefined
              }
              onClick={changeSortingColumn('userCount')}
              collapsing
              textAlign="center"
            >
              {formatMessage(m.userCount)}
            </Table.HeaderCell>

            {showInactive && (
              <Table.HeaderCell
                sorted={
                  sortingColumn === 'isActive' ? sortingDirection : undefined
                }
                onClick={changeSortingColumn('isActive')}
                collapsing
              >
                {formatMessage(m.active)}
              </Table.HeaderCell>
            )}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {currentPageRows.map((row) => (
            <Table.Row
              key={row.id}
              onClick={() => handleRowClick(row.id)}
              className="tw-cursor-pointer"
            >
              <Table.Cell collapsing>{row.name}</Table.Cell>
              <Table.Cell>
                <TeamLogo
                  src={`/api/teams/${row.id}/logo`}
                  className="tw-h-7 tw-object-contain"
                />
              </Table.Cell>
              <Table.Cell collapsing textAlign="center">
                {row.userCount}
              </Table.Cell>

              {showInactive && (
                <Table.Cell collapsing>
                  <div className="tw-flex tw-justify-center">
                    <ActiveIndicator active={row.isActive} />
                  </div>
                </Table.Cell>
              )}
            </Table.Row>
          ))}
          {!hasRows && (
            <Table.Row>
              <Table.Cell colSpan="3">
                <EmptyStateMessage message={formatMessage(m.noRow)} />
              </Table.Cell>
            </Table.Row>
          )}

          {hasRows && !matchFound && (
            <Table.Row>
              <Table.Cell colSpan="3">
                <EmptyStateMessage message={formatMessage(m.noMatchFound)} />
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </Fragment>
  );

  const pagination = hasMultiplePages && (
    <Pagination
      totalPages={pageCount}
      activePage={currentPage}
      onPageChange={handlePageChange}
    />
  );

  const bottomBarCenteringClass = isMobile ? 'tw-justify-center' : undefined;

  const bottomBar = (
    <div className={`tw-flex tw-items-baseline ${bottomBarCenteringClass}`}>
      {pagination}
      <div className="tw-flex-1" />
      <Button as={Link} to="/admin/teams/new" primary>
        {formatMessage(m.new)}
      </Button>
    </div>
  );

  return (
    <Fragment>
      <ApplicationSegment>
        {topBar}
        {table}
        {bottomBar}
      </ApplicationSegment>
    </Fragment>
  );
};
